// =============================================================================
// FAQ SECTION COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import type { ContentComponent } from '@/types';

interface FAQSectionProps {
  component: ContentComponent;
}

/**
 * FAQ Section Component
 */
export const FAQSection: React.FC<FAQSectionProps> = ({ component }) => {
  return <FAQSectionImpl component={component} />;
};

/**
 * FAQ Section Implementation
 */
const FAQSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const faqs = metadata?.faqs || [];
  const columns = metadata?.columns || 1;
  
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  
  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };
  
  const groupFAQsByCategory = () => {
    const grouped: Record<string, any[]> = {};
    faqs.forEach((faq: any) => {
      const category = faq.category || 'General';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(faq);
    });
    return grouped;
  };
  
  const groupedFAQs = groupFAQsByCategory();
  
  return (
    <section id={id} className="content-section faq-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`faq-grid grid-cols-${columns}`}>
          {Object.entries(groupedFAQs).map(([category, categoryFAQs]) => (
            <div key={category} className="faq-category">
              <div className="faq-category-header">
                <HelpCircle size={20} className="category-icon" />
                <h3 className="category-title">{category}</h3>
              </div>
              
              <div className="faq-list">
                {categoryFAQs.map((faq: any, index: number) => {
                  const globalIndex = faqs.findIndex((f: any) => f === faq);
                  const isOpen = openItems.has(globalIndex);
                  
                  return (
                    <div key={index} className="faq-item">
                      <button
                        className={`faq-question ${isOpen ? 'open' : ''}`}
                        onClick={() => toggleItem(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <span className="question-text">{faq.question}</span>
                        <div className="question-icon">
                          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </button>
                      
                      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
                        <div className="answer-content">
                          <p>{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { FAQSectionImpl };

